import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import i18next from "i18next";

import SharedButton from "../../components/Shared/SharedButton";
import SharedTooltip from "../../components/Shared/SharedTooltip";
import InsuranceClaimTable from "./InsuranceClaimTable";
import { debounce } from "../../helpers/debounce";
import Filters from "./Filters";
import FilterDetails from "../Invoice/FilterDetails";
import ExportInvoices from "../Invoice/ExportInvoices";
import supergitHIS from "../../api/supergitHIS";
import validSupergitHIS from "../../api/validSupergitHIS";
import { FaFileExcel, FaFileExport } from "react-icons/fa6";
import * as XLSX from "xlsx-js-style";
import { formatDate } from "../Transactions/TransactionsTable";
import SearchField from "../../components/Shared/SearchField";

export default function InsuranceClaimInvoice({
  ThirdParty = false,
  activeTab,
}) {
  const key = "insurance-claim-invoice";
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [filteredInvoices, setFilteredInvoices] = useState(null);
  const [listData, setListData] = useState([]);
  const [isListLoading, setIsListLoading] = useState(false);
  const [filterLoading, SetFiltersLoading] = useState(false);
  const [filtersDetails, setFiltersDetails] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const currentDate = moment().format("YYYY-MM-DD");
  const firstDayOfCurrentMonth = moment().startOf("month").format("YYYY-MM-DD");
  const lang = i18next.language;
  const [searchText, setSearchText] = useState("");

  const [filters, setFilters] = useState(
    localStorage.getItem(key)
      ? JSON.parse(localStorage.getItem(key))
      : {
        start_date: firstDayOfCurrentMonth,
        end_date: currentDate,
        branch_id: "",
        insurance_company_id: "",
        tpa: "",
        query: "",
        page: 1,
        per_page: 10,
        status: "pending",
      },
  );

  const fetchListData = async (payload = filters) => {
    setIsListLoading(true);
    try {
      const response = await validSupergitHIS.post(
        "insurance_claim_invoices/list",
        {
          ...payload,
          branch_id: String(payload?.branch_id),
          insurance_company_id: String(payload?.insurance_company_id),
        },
      );
      setListData(response.data || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setIsListLoading(false);
    }
  };

  useEffect(() => {
    fetchListData(filters);
  }, []);

  const handleSearch = (event) => {
    const value = event?.target?.value;
    setSearchText(value);
    debouncedSearch(value);
  };
  const handleClearSearch = () => {
    setSearchText("");

    const payload = {
      ...filters,
      query: "",
      page: 1,
    };

    setFilters(payload);
    fetchListData(payload);
  };
  const debouncedSearch = useCallback(
    debounce(async (value) => {
      const trimmedValue = value?.trim();

      const payload = {
        ...filters,
        query: trimmedValue,
      };

      setFilters(payload);
      fetchListData(payload);
    }, 500),
    [filters],
  );

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };
  const handleExcelExport = () => {
    if (!listData?.data || listData.data.length === 0) return;

    const fields = [
      {
        key: "date",
        label: t("Order Date") || "Order Date",
        format: formatDate,
      },
      { key: "invoice_no", label: t("Invoice No") || "Invoice No" },
      {
        key: "insurance_company.name_en",
        label: t("Insurance Company") || "Insurance Company",
      },
      { key: "branch.name_en", label: t("Branch") || "Branch" },
      { key: "total_gross", label: t("Total Gross") || "Total Gross" },
      { key: "total_discount", label: t("Total Discount") || "Total Discount" },
      {
        key: "total_deduction",
        label: t("Total Deduction") || "Total Deduction",
      },
      { key: "total_net", label: t("Total Net") || "Total Net" },
    ];

    const reportTitle =
      t("insurance claim invoice") || "Insurance Claim Invoice";
    const dateRange =
      filters?.start_date || filters?.end_date
        ? `${t("From")}: ${filters?.start_date || ""}  -  ${t("To")}: ${filters?.end_date || ""}`
        : "";
    const businessNameEn = user?.Business?.name_en || "";
    const businessNameAr = user?.Business?.name_ar || "";
    const displayBusinessName =
      lang === "ar"
        ? businessNameAr || businessNameEn
        : businessNameEn || businessNameAr;

    const headers = fields.map((f) => f.label);

    const aoa = [
      [`${displayBusinessName}`],
      [`${reportTitle}`],
      [`${dateRange}`],
      [],
      headers,
    ];

    listData.data.forEach((item) => {
      const rowArr = fields.map(({ key, format }) => {
        const val = getNestedValue(item, key);
        return format && val
          ? format(val)
          : val !== undefined && val !== null
            ? val
            : "";
      });
      aoa.push(rowArr);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);

    for (let c = 0; c < headers.length; c++) {
      const cellRef = XLSX.utils.encode_cell({ c, r: 4 });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          fill: { patternType: "solid", fgColor: { rgb: "D1D5DB" } },
          font: { bold: true },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }

    for (let r = 0; r < 3; r++) {
      const cellRef = XLSX.utils.encode_cell({ c: 0, r });
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          font: { bold: true, sz: r === 0 ? 16 : 14 },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }

    worksheet["!cols"] = [
      { wch: 25 },
      { wch: 20 },
      { wch: 50 },
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
    ];

    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: headers.length - 1 } },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      reportTitle.substring(0, 31),
    );

    XLSX.writeFile(workbook, `${reportTitle}.xlsx`);
  };

  return (
    <>
      <div className="">
        <div
          className={`flex ${!ThirdParty ? "justify-between" : "justify-end"
            } flex-col gap-3 sm:gap-0 sm:flex-row mb-2`}
        >
          {/* <SharedTooltip
            content={t("Keep_track_of_your_invoices")}
            position="left"
            id="Items"
          /> */}
          {/* {!ThirdParty && (
            <span
              data-tooltip-id="Items"
              className="font-semibold text-black/70 text-xl mt-2"
            >
              {t("insurance_claim_invoice")}
              <span className="italic text-lg ml-1">
                <span className="text-xs font-bold italic">
                  ( {listData?.total_record || 0} {t("Entries")})
                </span>
              </span>
            </span>
          )} */}
          <div
            className={`flex items-center gap-2 ${isRTL ? "mr-auto" : "ml-auto"}`}
          >
            <div className="flex gap-2">
              <SearchField
                className="w-full md:!w-[300px]"
                placeholder={t(" Search by invoice number")}
                value={searchText}
                onChange={handleSearch}
                handleClearClick={handleClearSearch}
              />
            </div>
            <SharedButton
              label={t("Credit notes")}
              icon="pi pi-credit-card"
              onClick={() => {
                if (ThirdParty) {
                  navigate("/zatca/thirdparty/create-insurance-claim-invoice", {
                    state: {
                      ThirdParty: true,
                      invoiceData: null,
                      creditNote: true,
                    },
                  });
                } else {
                  navigate("/manager/create-insurance-claim-invoice", {
                    state: {
                      ThirdParty: false,
                      invoiceData: null,
                      creditNote: true,
                    },
                  });
                }
              }}
              className="bg-primary primary-btn"
            />

            <SharedButton
              label={`${t("create")} ${t("insurance_claim_invoice")}`}
              icon="pi pi-plus"
              onClick={() =>
                ThirdParty
                  ? navigate(
                    "/zatca/thirdparty/create-insurance-claim-invoice",
                    { state: { ThirdParty: true, module: activeTab } },
                  )
                  : navigate("/manager/create-insurance-claim-invoice", {
                    state: {
                      ThirdParty: false,
                    },
                  })
              }
              className="bg-primary primary-btn"
            />

            <SharedButton
              title={t("Export to Excel")}
              icon="pi pi-file-excel"
              onClick={handleExcelExport}
            />
          </div>
        </div>

        <Filters
          storageKey={key}
          filters={filters}
          setFilters={setFilters}
          setSearchText={setSearchText}
          filterLoading={filterLoading}
          handleAddFilters={() => { }}
          handleResetFilters={() => { }}
          filteredInvoices={filteredInvoices}
          fetchListData={fetchListData}
          loading={isListLoading}
        />
        <InsuranceClaimTable
          companyInvoicesList={listData}
          activeTab={activeTab}
          ThirdParty={ThirdParty}
          loading={isListLoading}
          filters={filters}
          setFilters={setFilters}
          fetchListData={fetchListData}
          setFilteredInvoices={setFilteredInvoices}
          SetFiltersLoading={SetFiltersLoading}
          setFiltersDetails={setFiltersDetails}
        />
      </div>
    </>
  );
}
